import type { Deal, DealStageCriterion } from "../types";
import { isStageGatedByCriterion } from "./stageConfig";

/**
 * Almikia Epic E2: POSI (Perfil, Objetivos, eStrategias, Issues) is the set of
 * 4 discovery questions (intención, presupuesto, forma de pago, quién
 * decide) a lead must answer before it can reach a POSI-gated stage. This is
 * the single shared pure helper for that rule — the FakeRest provider
 * (`providers/fakerest/dataProvider.ts`) and the UI both import from here
 * instead of re-implementing the completeness/gating check. Must stay in
 * lockstep with TASK-008's DB trigger `enforce_deals_posi_gate()`
 * (`supabase/schemas/02_functions.sql`): same 4 fields, same trim-then-blank
 * check, same message.
 *
 * Almikia Epic EP (TASK-103): which stages are gated is no longer the fixed
 * `POSI_GATED_STAGES` list — it is resolved through
 * `stageConfig.ts`'s `isStageGatedByCriterion` against the configured
 * `deal_stage_criteria` rows for the `'posi_complete'` criterion, which keeps
 * the E2 fallback (same 4 stages gated) when `criteria` is empty (loading,
 * failed to load, or never seeded).
 */

export type PosiAnswers = Pick<
  Deal,
  "posi_intent" | "posi_budget" | "posi_payment_method" | "posi_decision_maker"
>;

/** Exact message the DB trigger raises; the FakeRest parity check must match it verbatim. */
export const POSI_INCOMPLETE_MESSAGE =
  "Completa las 4 preguntas POSI antes de mover el lead a Showing";

/**
 * i18n key for the translated POSI-gate message shown in the deal form and
 * the kanban board (TASK-015). Its Spanish (Mexico) text
 * (`spanishCrmMessages.ts`) must stay identical to `POSI_INCOMPLETE_MESSAGE`
 * so a client-side block and a server-side rejection read the same to the
 * user.
 */
export const POSI_GATE_MESSAGE_KEY = "resources.deals.posi_gate_blocked";

const isBlank = (value: string | null | undefined): boolean =>
  value == null || value.trim() === "";

/** True only when all 4 POSI answers are present and non-blank after trim. */
export function isPosiComplete(
  answers: Partial<PosiAnswers> | null | undefined,
): boolean {
  if (!answers) return false;
  return (
    !isBlank(answers.posi_intent) &&
    !isBlank(answers.posi_budget) &&
    !isBlank(answers.posi_payment_method) &&
    !isBlank(answers.posi_decision_maker)
  );
}

/**
 * How many of the 4 POSI answers are present and non-blank after trim; used
 * to render the lead show page's segmented completeness ProgressBar
 * (Almikia design-system.md section 3, "N of 4" segmented bar). A blank or
 * whitespace-only value counts as unanswered, exactly like {@link isPosiComplete}.
 */
export function countPosiAnswers(
  answers: Partial<PosiAnswers> | null | undefined,
): number {
  if (!answers) return 0;
  return [
    answers.posi_intent,
    answers.posi_budget,
    answers.posi_payment_method,
    answers.posi_decision_maker,
  ].filter((value) => !isBlank(value)).length;
}

/**
 * True when `stage` requires a completed POSI: resolved through
 * `isStageGatedByCriterion` (TASK-102) against the configured
 * `deal_stage_criteria` rows for the `'posi_complete'` criterion. `criteria`
 * defaults to `[]`, which resolves to the E2 fallback list (showing, cierre,
 * administracion, venta_cerrada) -- the behavior while configuration is
 * loading, failed to load, or was never seeded, and the behavior of every
 * existing call site that has not been updated to pass criteria yet.
 */
export function stageRequiresPosi(
  stage: string | null | undefined,
  criteria: readonly DealStageCriterion[] = [],
): boolean {
  if (stage == null) return false;
  return isStageGatedByCriterion(stage, "posi_complete", criteria);
}

/**
 * True when a lead is allowed to move into `targetStage`: either the target
 * stage does not require POSI, or it does and `answers` is complete.
 */
export function canMoveToStage(
  targetStage: string | null | undefined,
  answers: Partial<PosiAnswers> | null | undefined,
  criteria: readonly DealStageCriterion[] = [],
): boolean {
  return !stageRequiresPosi(targetStage, criteria) || isPosiComplete(answers);
}

/**
 * Field-level validator for the deal Create/Edit form's `stage` input:
 * blocks the save when the submitted stage's `'posi_complete'` criterion is
 * enabled (or E2-gated by fallback) and the answers are incomplete.
 * `previousStage` is the stage the record had before this edit; pass
 * `undefined` on create (no prior row, so any gated stage always requires a
 * complete POSI). When the stage is left unchanged the save is never
 * blocked, even with an incomplete POSI or a legacy stage value.
 */
export function validateDealPosiGate(
  stage: string | null | undefined,
  answers: Partial<PosiAnswers> | null | undefined,
  previousStage: string | null | undefined,
  criteria: readonly DealStageCriterion[] = [],
): { message: string; args: Record<string, never> } | undefined {
  const stageChanged = previousStage === undefined || stage !== previousStage;
  if (stageChanged && !canMoveToStage(stage, answers, criteria)) {
    return { message: POSI_GATE_MESSAGE_KEY, args: {} };
  }
  return undefined;
}
